'use client';

import React from 'react';
import classes from './NeumoParagraph.module.scss';
import { Container } from '@mantine/core';

type NeumoParagraphSubSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
};

type NeumoParagraphSection = {
  id: string;
  title: string;
  paragraphs?: string[];
  bullets?: string[];
  subsections?: NeumoParagraphSubSection[];
};

export type NeumoParagraphSlice = {
  type: 'neumo-paragraph';
  badge?: string;
  title: string;
  description?: string;
  updatedAt?: string;
  intro?: string[];
  sections?: NeumoParagraphSection[];

  // Optional: replace tokens like {{SIRET}}
  variables?: Record<string, string>;
};

function applyVariables(text: string, vars?: Record<string, string>) {
  if (!text || !vars) {
    return text;
  }
  return text.replace(/\{\{\s*([A-Z0-9_]+)\s*\}\}/g, (_, key) => vars[key] ?? `{{${key}}}`);
}

function renderParagraphs(paragraphs?: string[], vars?: Record<string, string>) {
  if (!paragraphs?.length) {
    return null;
  }
  return paragraphs.map((p, idx) => (
    <p key={idx} className={classes.paragraph}>
      {applyVariables(p, vars)}
    </p>
  ));
}

function renderBullets(bullets?: string[], vars?: Record<string, string>) {
  if (!bullets?.length) {
    return null;
  }
  return (
    <ul className={classes.list}>
      {bullets.map((b, idx) => (
        <li key={idx}>{applyVariables(b, vars)}</li>
      ))}
    </ul>
  );
}

export function NeumoParagraph({ data, id }: { data: NeumoParagraphSlice; id?: string }) {
  if (!data) {
    return null;
  }

  const rootId = id ?? 'neumo-paragraph';
  const vars = data.variables;

  return (
    <Container className={classes.container}>
      <section id={rootId} className={classes.section} aria-labelledby={`${rootId}-title`}>
        <div className={classes.container}>
          <header className={classes.header}>
            {data.badge && <div className={classes.badge}>{applyVariables(data.badge, vars)}</div>}

            <h1 id={`${rootId}-title`} className={classes.title}>
              {applyVariables(data.title, vars)}
            </h1>

            {data.description && (
              <p className={classes.description}>{applyVariables(data.description, vars)}</p>
            )}

            {data.updatedAt && (
              <p className={classes.updatedAt}>{applyVariables(data.updatedAt, vars)}</p>
            )}

            {Array.isArray(data.intro) && data.intro.length > 0 && (
              <div className={classes.intro}>{renderParagraphs(data.intro, vars)}</div>
            )}
          </header>

          {data.sections?.length ? (
            <div className={classes.content}>
              {data.sections.map((section) => (
                <article key={section.id} className={classes.block} id={section.id}>
                  <h2 className={classes.h2}>{applyVariables(section.title, vars)}</h2>

                  {renderParagraphs(section.paragraphs, vars)}
                  {renderBullets(section.bullets, vars)}

                  {section.subsections?.map((sub) => (
                    <div key={sub.id} className={classes.subBlock} id={sub.id}>
                      <h3 className={classes.h3}>{applyVariables(sub.title, vars)}</h3>
                      {renderParagraphs(sub.paragraphs, vars)}
                      {renderBullets(sub.bullets, vars)}
                    </div>
                  ))}
                </article>
              ))}
            </div>
          ) : null}
        </div>
      </section>
    </Container>
  );
}
